/*
Get the users conversation and dispatch the action to set it on the store
 */
import type { PayloadAction } from "@reduxjs/toolkit";
import { signedOut } from "@somewear/auth";
import { fileActions } from "@somewear/files";
import { grpc, someGrpc } from "@somewear/grpc";
import type { ConversationTimestamp, IGetMessagesResponse } from "@somewear/messaging";
import {
	getReadTimestamps,
	messageActions,
	setConversationTimestampRequest,
	setConversationTimestampSuccess,
	setReadTimestamps,
	setWorkspaceTimestampRequest,
	setWorkspaceTimestampSuccess,
} from "@somewear/messaging";
import type {
	ActionSetEpic,
	IMessage,
	MessageRequestWithStateAndTmpId,
	MessagesRequestPayload,
	UploadFileRequest,
	UploadFileResponse,
} from "@somewear/model";
import {
	actionSetEpicHandlerBuilder,
	createActionSetEpicHandler,
	createMergingActionSetEpicHandler,
} from "@somewear/model";
import { UserDataSyncClient } from "@somewear/user-data-sync";
import type { Action } from "redux";
import type { Epic } from "redux-observable";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from "rxjs/operators";

/*export const apiConversationsEpic: Epic<Action<string>> = (action$) =>
	action$.pipe(
		ofType(apiConversationsRequest.type),
		switchMap(() =>
			Api.getConversations().pipe(
				mergeMap((conversationResponse) =>
					from([
						addContactsFromResponses(conversationResponse.toObject().conversationsList),
						apiConversationsSuccess(conversationResponse.toObject().conversationsList),
						getReadTimestamps(),
					])
				),
				takeUntil(action$.pipe(ofType(signedOut.type))),
				catchError((error) => of(apiConversationsError(error)))
			)
		)
	);*/

/*
Get the messages for a conversation and dispatch the action to set it on the store
 */

export const apiMessagesEpic: ActionSetEpic<MessagesRequestPayload, IGetMessagesResponse> = (
	action$,
	state$,
) => {
	return createMergingActionSetEpicHandler(
		action$,
		state$,
		messageActions.get,
		(payload) => {
			return of(payload.data).pipe(
				filter((data) => data.conversationInfo.conversationId !== "null"),
				switchMap((r) => of(r)),
				mergeMap((request) =>
					grpc.prepareRequestWithPayload(someGrpc.getMessages, request).pipe(
						map((response) => {
							return {
								conversationInfo: request.conversationInfo,
								messages: response.toObject().messagesList,
							};
						}),
					),
				),
			);
		},
		(payload) => {
			return {
				onFulfilled: "",
				onPending: "",
				onRejected: "Error loading messages",
			};
		},
	);
};

/*
Send a message for a conversation and dispatch the action to set it on the store once the response comes back
 */

export const sendMessageEpic: ActionSetEpic<MessageRequestWithStateAndTmpId, IMessage> = (
	action$,
	state$,
) => {
	return createActionSetEpicHandler(action$, state$, messageActions.send, (payload) =>
		grpc
			.prepareRequestWithPayload(someGrpc.sendMessage, payload.data)
			.pipe(map((r) => r.toObject().message)),
	);
};

/*
 * Upload a file to S3 and dispatch the action to set it on the store once the response comes back
 */
export const uploadFileEpic: ActionSetEpic<UploadFileRequest, UploadFileResponse> = (
	action$,
	state$,
) => {
	return createActionSetEpicHandler(
		action$,
		state$,
		fileActions.uploadFile,
		(payload) => {
			return grpc.prepareRequestWithPayload(someGrpc.uploadFile, payload.data).pipe(
				mergeMap((response) => {
					const { signedUploadUrl, file: fileMetadata } = response.toObject();
					if (!fileMetadata) return of();

					const contentType = fileMetadata.mimeType || "application/octet-stream";
					// Perform the actual upload client -> cloud bucket.
					return ajax({
						url: signedUploadUrl,
						method: "PUT",
						headers: {
							// We have to set a string with some value here, otherwise rxJS automatically sets
							// the type as application/x-www-form-urlencoded, which S3 cannot parse for some types.
							// GCS requires the content type to be set explicitly, and S3 doesn't mind.
							"Content-Type": contentType,
						},
						body: payload.data.file,
						async: true,
						crossDomain: true,
						timeout: 0,
					}).pipe(
						map(() => {
							return {
								signedUploadUrl,
								fileMetadata: fileMetadata,
							};
						}),
					);
				}),
			);
		},
		(payload) => {
			return {
				onFulfilled: "",
				onPending: "",
				onRejected: "Error uploading file",
			};
		},
	);
};

/*
 * Get the workspace/conversation files and dispatch the action to set it on the store
 */
export const getConversationFilesEpic = actionSetEpicHandlerBuilder(
	fileActions.getFiles,
	(payload) =>
		grpc.prepareRequestWithPayload(someGrpc.getConversationFiles, payload.data).pipe(
			map((r) => {
				return r.toObject().filesList;
			}),
		),
	{
		onFulfilled: "",
		onPending: "",
		onRejected: "Error getting files",
	},
);

/*
 * Get the voice recordings and dispatch the action to set it on the store
 */
export const getVoiceRecordingsEpic = actionSetEpicHandlerBuilder(
	fileActions.getVoiceRecordings,
	(payload) =>
		grpc.prepareRequestWithPayload(someGrpc.getVoiceRecordings, payload.data).pipe(
			map((r) => {
				return r.toObject().filesList;
			}),
		),
	{
		onFulfilled: "",
		onPending: "",
		onRejected: "Error getting voice recordings",
	},
);

export { downloadFileEpic } from "./fileDownload.epic";

/*export const apiSendMessageEpic: ActionSetEpic<MessageResponseWithState, MessageResponseWithTmpId> =
	(action$, state$) => {
		const requestBuilder = (
			payload: IRequestPayload<MessageResponseWithState>
		): Observable<MessageResponse | undefined> => {
			return Api.sendMessage(payload.data);
		};

		return createActionSetEpicHandler(action$, state$, messageActions.send, requestBuilder);
	};*/

/*
Delete a conversation. Filter conversation id = null since that's is the id for a new conversation
 */
/*
type ApiDeleteConversationInActions = PayloadAction<string>;
type ApiDeleteConversationOutActions = Action | PayloadAction<IError>;
export const apiDeleteConversationEpic: Epic<
	ApiDeleteConversationInActions | ApiDeleteConversationOutActions,
	ApiDeleteConversationOutActions
> = (action$) =>
	action$.pipe(
		ofType(apiDeleteConversationRequest.type),
		map((action) => action as ApiDeleteConversationInActions),
		filter((action) => action.payload !== "null"),
		switchMap((action) =>
			Api.deleteConversation(action.payload).pipe(
				map((conversation) => apiDeleteConversationSuccess()),
				takeUntil(action$.pipe(ofType(signedOut.type))),
				catchError((error) => of(apiDeleteConversationError(error)))
			)
		)
	);
*/

/*type ApiCreateConversationInActions = PayloadAction<MessageDto.AsObject>;
type ApiCreateConversationOutActions =
	| PayloadAction<MessageRequestWithStateAndTmpId>
	| Action
	| PayloadAction<
			| ConversationResponseWithMessage.AsObject[]
			| TrackingIntervalResponseWithLocation.AsObject[]
	  >
	| PayloadAction<ConversationResponseWithMessage.AsObject[]>
	| PayloadAction<IError>;
export const apiCreateConversationEpic: Epic<
	ApiCreateConversationInActions | ApiCreateConversationOutActions,
	ApiCreateConversationOutActions,
	RootState
> = (action$, state$) =>
	action$.pipe(
		ofType(apiCreateConversationRequest.type),
		map((action) => action as ApiCreateConversationInActions),
		switchMap((action) =>
			Api.createConversation(action.payload).pipe(
				mergeMap((conversation) => {
					let conversationId = conversation.getConversation()!.getId();
					// if there was already a conversation just add the message
					if (state$.value.messages.ids.includes(conversationId)) {
						return from([
							messageActions.send.fulfilled({
								requestId: "-1",
								data: conversation.getMostRecentMessage()!.toObject(),
							}),
							apiCreateConversationSuccess(conversation.toObject()),
						]);
					} else {
						return from([
							addContactsFromResponses([conversation.toObject()]),
							apiConversationsSuccess([conversation.toObject()]),
							apiCreateConversationSuccess(conversation.toObject()),
						]);
					}
				}),
				takeUntil(action$.pipe(ofType(signedOut.type))),
				catchError((error) => of(apiCreateConversationError(error)))
			)
		)
	);*/

export const getReadTimestampsEpic: Epic<Action> = (action$) =>
	action$.pipe(
		ofType(getReadTimestamps.type),
		switchMap(() => {
			return UserDataSyncClient.getReadTimestamps().pipe(
				map((timestamps) => setReadTimestamps(timestamps)),
				takeUntil(action$.pipe(ofType(signedOut.type))),
				catchError(() => of(setReadTimestamps({ conversations: {}, workspaces: {} }))),
			);
		}),
	);

export const setConversationTimestampEpic: Epic<PayloadAction<ConversationTimestamp>> = (action$) =>
	action$.pipe(
		ofType(setConversationTimestampRequest.type),
		switchMap((action: PayloadAction<ConversationTimestamp>) => {
			UserDataSyncClient.setConversationTimestamp(
				action.payload.conversationId,
				action.payload.timestamp,
			);
			return of(setConversationTimestampSuccess(action.payload));
		}),
	);

export const setWorkspaceTimestampEpic: Epic<PayloadAction<ConversationTimestamp>> = (action$) =>
	action$.pipe(
		ofType(setWorkspaceTimestampRequest.type),
		switchMap((action: PayloadAction<ConversationTimestamp>) => {
			UserDataSyncClient.setWorkspaceTimestamp(
				action.payload.conversationId,
				action.payload.timestamp,
			);
			return of(setWorkspaceTimestampSuccess(action.payload));
		}),
	);
